import { useEffect, useRef } from "react";

import { useSearchParams } from "react-router-dom";

import { URLKeys } from "src/router/SearchParams";

/**
 * Whether a decision record is the one an id names. A parent decision answers
 * for its children, so a link to a child opens the row that contains it. Both
 * the table's row lookup and the pane's "do we already have this one?" check
 * read this — they must agree, or the pane filters when it need not, or the
 * table fails to find what the pane fetched.
 */
export const decisionMatchesId = (
  decision: { id: string; parent_decision_id?: string | null },
  decisionId: string,
): boolean =>
  decision.id === decisionId || decision.parent_decision_id === decisionId;

/**
 * Reacts to the decision id the URL names — a followed link, a pasted URL, or
 * the id the pane itself writes when a row is selected. Each id is handed to
 * `onDecisionId` until it reports success (the row may not be in the table
 * yet), once per id; telling an id the pane already shows apart from a link to
 * it is `onDecisionId`'s job. Clearing the id (deselecting a row) forgets it,
 * so a later link to the same decision opens it again.
 */
export const useDeeplinkedDecisionId = (
  onDecisionId: (decisionId: string) => boolean,
): void => {
  const [searchParams] = useSearchParams();
  const urlDecisionId = searchParams.get(URLKeys.DecisionId);
  const handledDecisionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!urlDecisionId) {
      handledDecisionIdRef.current = null;
      return;
    }
    if (handledDecisionIdRef.current === urlDecisionId) {
      return;
    }
    if (onDecisionId(urlDecisionId)) {
      handledDecisionIdRef.current = urlDecisionId;
    }
  }, [urlDecisionId, onDecisionId]);
};
